import { useState, useEffect, useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

// --- Project data ---

interface Project {
  category: string;
  summary: string;
  description: string;
  tech: string[];
  links: { label: string; href: string }[];
}

const projects: Project[] = [
  {
    category: 'Healthcare AI',
    summary: 'Credentialing automation, contract intelligence, and compliance infrastructure for healthcare organizations.',
    description: 'Document classification pipelines using vision-language models with confidence-based routing and human-in-the-loop verification. Semantic contract analysis with clause classification, obligation extraction, and confusability detection. Structured data extraction from clinical and administrative documents. Production deployments on SOC 2-compliant infrastructure with automated compliance routing.',
    tech: ['VLMs', 'NLP', 'AWS', 'SOC 2'],
    links: [],
  },
  {
    category: 'NLP & Document Intelligence',
    summary: 'Corpus construction, semantic analysis, document classification, and structured extraction at scale.',
    description: 'End-to-end NLP pipelines: document ingestion with OCR and vision-language models, semantic classification, entity and relation extraction, knowledge graph construction, and embedding-based search. Corpus construction from historical archives to modern discourse. Diachronic semantic analysis, morphological processing, and LLM-based OCR correction. Turning unstructured text into structured, queryable data.',
    tech: ['NLP', 'knowledge graphs', 'embeddings', 'OCR'],
    links: [],
  },
  {
    category: 'Explainability & Compliance',
    summary: 'Audit tooling and compliance reporting for regulated AI systems — EU AI Act, GDPR, FCRA, FDA.',
    description: 'Making AI systems auditable, interpretable, and regulation-ready. Multi-method explainability across ML frameworks through a unified interface. Constraint and governance layers for generative models. Compliance report generation against EU AI Act, GDPR Article 22, US FCRA, and FDA AI/ML medical device guidelines. Built for high-stakes systems where auditability is a requirement, not a feature.',
    tech: ['explainability', 'compliance', 'SHAP', 'LIME'],
    links: [
      { label: 'GitHub', href: 'https://github.com/neumanns-workshop/explainerkit' },
    ],
  },
];

// --- WorkItem sub-component ---

function WorkItem({ project }: { project: Project }) {
  const [open, setOpen] = useState(false);
  const panelId = `${useId()}-panel`;

  return (
    <div className="group">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full text-left flex items-start gap-3 py-3"
      >
        <span className="inline-block w-2.5 h-2.5 bg-accent mt-1.5 flex-shrink-0" aria-hidden="true" />
        <div className="flex-1">
          <h3 className="font-sans text-xs tracking-[0.2em] uppercase text-vellum group-hover:text-accent transition-colors">
            {project.category}
          </h3>
          <p className="mt-2 text-sm text-vellum-muted leading-relaxed">
            {project.summary}
          </p>
        </div>
        <span
          className="font-sans text-xs text-vellum-muted mt-1.5 flex-shrink-0 transition-transform duration-200"
          style={{ transform: open ? 'rotate(45deg)' : 'none' }}
          aria-hidden="true"
        >
          +
        </span>
      </button>

      {open && (
        <div id={panelId} className="ml-[22px] pl-3 border-l border-rule pb-4 animate-fade-in">
          <p className="text-sm text-vellum leading-relaxed mb-3">
            {project.description}
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {project.tech.map((t) => (
              <span key={t} className="font-mono text-xs text-vellum-muted">
                {t}
              </span>
            ))}
          </div>
          {project.links.length > 0 && (
            <div className="flex gap-4">
              {project.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs text-accent hover:underline"
                >
                  {link.label} &rarr;
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- HomePage ---

export default function HomePage() {
  const location = useLocation();

  // Handle cross-route anchor scrolling from Header
  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string })?.scrollTo;
    if (scrollTo) {
      // Clear the state so back-navigation doesn't re-trigger
      window.history.replaceState({}, '');
      // Wait one frame for DOM to render
      requestAnimationFrame(() => {
        document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [location.state]);

  return (
    <>
      <SEO
        description="Computational linguist and NLP engineer building applied systems for healthcare, document intelligence, and regulated AI."
        keywords="computational linguistics, NLP engineering, healthcare AI, document intelligence, regulated AI, clinical NLP, PhonoLex, explainability"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ProfessionalService',
          name: "Neumann's Workshop",
          description: 'Computational linguist and NLP engineer building applied systems for healthcare, document intelligence, and regulated AI.',
          url: 'https://neumannsworkshop.com',
          contactPoint: {
            '@type': 'ContactPoint',
            url: 'https://neumannsworkshop.com/#contact',
          },
          founder: {
            '@type': 'Person',
            name: 'Jared Neumann',
          },
        }}
      />

      <div className="px-6 md:px-12 lg:px-16 pt-24 lg:max-w-[65%]">
        {/* Hero */}
        <section className="pt-16 md:pt-24 pb-32 md:pb-40 animate-fade-in">
          <h1 className="font-serif text-3xl md:text-4xl mb-6">
            Neumann&rsquo;s Workshop
          </h1>
          <p className="font-serif text-base md:text-lg text-vellum-muted leading-relaxed max-w-lg">
            Computational linguist &amp; NLP engineer building applied systems for healthcare, document intelligence, and regulated AI.
          </p>
          <div className="mt-8 w-10 h-px bg-rule" />
        </section>

        {/* Solutions */}
        <section id="work" className="pb-32 md:pb-40">
          <h2 className="font-sans text-xs tracking-[0.2em] uppercase text-vellum-muted mb-8">
            Solutions
          </h2>
          <div className="w-10 h-px bg-rule mb-8" />
          <div className="space-y-2">
            {projects.map((project) => (
              <WorkItem key={project.category} project={project} />
            ))}
          </div>
        </section>

        {/* Flagship product */}
        <section className="pb-32 md:pb-40">
          <a
            href="https://phonolex.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group block border border-rule p-6 md:p-8 hover:border-accent transition-colors"
          >
            <span className="font-sans text-xs tracking-[0.2em] uppercase text-vellum-muted">
              Flagship product
            </span>
            <h2 className="font-serif text-2xl mt-2 group-hover:text-accent transition-colors">
              PhonoLex
            </h2>
            <p className="font-serif text-base text-vellum-muted leading-relaxed mt-3">
              Phonological analysis, governed language generation, and psycholinguistic data for SLPs, linguists, and educators. Build custom word lists from 44,000 English words across 35 psycholinguistic properties, generate constrained content with a real-time compliance engine on T5Gemma, and explore contrastive sets, sound similarity, and cognitive associations. Speech error annotation from audio is the next chapter.
            </p>
            <span className="inline-block font-mono text-xs text-accent mt-4">
              phonolex.com &rarr;
            </span>
          </a>
        </section>

        {/* Edutainment */}
        <section className="pb-16 md:pb-24">
          <p className="font-serif text-base text-vellum-muted leading-relaxed">
            Also: edutainment side projects at{' '}
            <Link to="/edutainment" className="text-accent hover:underline">
              Galaxy Brain Entertainment
            </Link>
            {' '}&mdash; Synapse, Eureka, and AHAB.
          </p>
        </section>

        {/* Contact */}
        <section id="contact" className="pb-16 md:pb-24">
          <h2 className="font-sans text-xs tracking-[0.2em] uppercase text-vellum-muted mb-8">
            Let&rsquo;s work together.
          </h2>
          <div className="w-10 h-px bg-rule mb-8" />
          <ContactForm />
          <div className="mt-8">
            <a
              href="https://www.linkedin.com/in/jmneumann"
              target="_blank"
              rel="noopener noreferrer"
              className="font-sans text-sm text-vellum-muted hover:text-accent transition-colors"
            >
              More about me on LinkedIn &rarr;
            </a>
          </div>
        </section>
      </div>
    </>
  );
}
